import { useEffect, useState } from 'react';
import { useT } from '../i18n/i18n';
import { useLearner } from '../learners/LearnerContext';
import { TARGET_STATUS_LABEL, fetchGoals, type Goal, type Target } from './api';
import { addDays, addMonths, monthEnd, monthStart, parseDay, today, useDates } from './dates';
import { termOf } from './ProgressView';
import './progress.css';

type Zoom = 'term' | 'year';
type Range = { from: string; to: string };
type Span = { start: string; end: string };
type Event = { day: string; kind: 'start' | 'review' | 'by' | 'achieved' | 'goal'; goal: Goal; target: Target | null };

const NARROW = '(max-width: 720px)';

function yearOf(day: string): Range {
  const d = parseDay(day);
  const y = d.getMonth() >= 8 ? d.getFullYear() : d.getFullYear() - 1;
  return { from: `${y}-09-01`, to: `${y + 1}-08-31` };
}

function rangeFor(zoom: Zoom, day: string): Range {
  return zoom === 'term' ? termOf(day) : yearOf(day);
}

function shift(zoom: Zoom, r: Range, dir: -1 | 1): Range {
  if (zoom === 'year') return yearOf(addMonths(r.from, 12 * dir));
  if (dir < 0) return termOf(addDays(r.from, -1));
  const next = addDays(r.to, 1);
  return termOf(parseDay(next).getMonth() === 7 ? `${next.slice(0, 4)}-09-01` : next);
}

function spanOf(x: Target): Span | null {
  const end = x.reviewDate ?? x.byDate ?? x.achievedAt ?? (x.startDate ? new Date().toISOString().slice(0, 10) : null);
  const dated = [x.byDate, x.reviewDate].filter((v): v is string => !!v).sort();
  const start = x.startDate ?? (dated.length ? addDays(dated[0], -84) : null);
  if (!start || !end) return null;
  return start <= end ? { start, end } : { start: end, end: start };
}

function useNarrow(): boolean {
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia(NARROW).matches);
  useEffect(() => {
    const mq = window.matchMedia(NARROW);
    const on = () => setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return narrow;
}

function months(r: Range): string[] {
  const out: string[] = [];
  for (let m = monthStart(r.from); m <= r.to; m = addMonths(m, 1)) out.push(m);
  return out;
}

export default function TimelineView() {
  const t = useT();
  const d = useDates();
  const { learnerId } = useLearner();
  const narrow = useNarrow();
  const now = today();
  const [zoom, setZoom] = useState<Zoom>('term');
  const [range, setRange] = useState<Range>(() => termOf(now));
  const [goals, setGoals] = useState<Goal[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    if (!learnerId) return;
    let live = true;
    setError(null);
    fetchGoals(learnerId)
      .then((g) => { if (live) setGoals(g); })
      .catch((e) => { if (live) setError(e instanceof Error && e.message ? e.message : t('The plan could not be loaded just now.')); });
    return () => { live = false; };
  }, [learnerId]);

  const total = parseDay(addDays(range.to, 1)).getTime() - parseDay(range.from).getTime();
  const pct = (day: string) => Math.min(100, Math.max(0, ((parseDay(day).getTime() - parseDay(range.from).getTime()) / total) * 100));
  const inRange = (day: string | null) => !!day && day >= range.from && day <= range.to;
  const monthList = months(range);
  const monthName = (m: string) => parseDay(m).toLocaleDateString(d.locale, { month: zoom === 'term' ? 'long' : 'short' });

  const rows = (goals ?? [])
    .map((g) => ({
      goal: g,
      targets: g.targets
        .map((x) => ({ target: x, span: spanOf(x) }))
        .filter(({ span }) => !span || (span.start <= range.to && span.end >= range.from)),
    }))
    .filter((r) => r.targets.length > 0 || inRange(r.goal.byDate));

  const chosen = (() => {
    for (const g of goals ?? []) for (const x of g.targets) if (x.id === selected) return { goal: g, target: x };
    return null;
  })();

  function setZoomTo(z: Zoom) {
    setZoom(z);
    setRange(rangeFor(z, inRange(now) ? now : range.from));
  }

  const status = (x: Target) => t(TARGET_STATUS_LABEL[x.status]);

  function barName(x: Target, s: Span): string {
    return x.achievedAt
      ? t('{target}. From {start} to {end}. {status}, achieved on {date}.', { target: x.statement, start: d.short(s.start), end: d.short(s.end), status: status(x), date: d.short(x.achievedAt) })
      : t('{target}. From {start} to {end}. {status}.', { target: x.statement, start: d.short(s.start), end: d.short(s.end), status: status(x) });
  }

  function grid() {
    return (
      <>
        {monthList.map((m) => m > range.from && <span key={m} className="planner-timeline-month-line" style={{ insetInlineStart: `${pct(m)}%` }} aria-hidden="true" />)}
        {inRange(now) && <span className="planner-timeline-today-line" style={{ insetInlineStart: `${pct(now)}%` }} aria-hidden="true" />}
      </>
    );
  }

  const events: Event[] = [];
  for (const { goal, targets } of rows) {
    if (inRange(goal.byDate)) events.push({ day: goal.byDate as string, kind: 'goal', goal, target: null });
    for (const { target: x } of targets) {
      if (inRange(x.startDate)) events.push({ day: x.startDate as string, kind: 'start', goal, target: x });
      if (inRange(x.reviewDate)) events.push({ day: x.reviewDate as string, kind: 'review', goal, target: x });
      if (inRange(x.byDate) && x.byDate !== x.reviewDate) events.push({ day: x.byDate as string, kind: 'by', goal, target: x });
      if (inRange(x.achievedAt)) events.push({ day: x.achievedAt as string, kind: 'achieved', goal, target: x });
    }
  }
  events.sort((a, b) => a.day.localeCompare(b.day));
  const EVENT_LABEL: Record<Event['kind'], string> = { start: t('Starts'), review: t('Review'), by: t('Due'), achieved: t('Achieved'), goal: t('Goal due') };

  return (
    <div className="planner-timeline">
      <div className="planner-timeline-bar-tools">
        <div className="toolbar" role="group" aria-label={t('Zoom')}>
          <button type="button" className="btn secondary" aria-pressed={zoom === 'term'} onClick={() => setZoomTo('term')}>{t('Term')}</button>
          <button type="button" className="btn secondary" aria-pressed={zoom === 'year'} onClick={() => setZoomTo('year')}>{t('Year')}</button>
        </div>
        <div className="toolbar">
          <button type="button" className="btn secondary" onClick={() => setRange(shift(zoom, range, -1))}>{t('Previous')}</button>
          <button type="button" className="btn secondary" onClick={() => setRange(rangeFor(zoom, now))}>{t('Today')}</button>
          <button type="button" className="btn secondary" onClick={() => setRange(shift(zoom, range, 1))}>{t('Next')}</button>
        </div>
      </div>
      <h3 className="planner-timeline-range" aria-live="polite">{t('{from} to {to}', { from: d.short(range.from), to: d.short(range.to) })}</h3>

      {error && <div className="error" role="alert">{error}</div>}
      {!goals && !error && <p className="muted">{t('Loading…')}</p>}
      {goals && rows.length === 0 && <p className="muted">{t('No targets fall in these dates.')}</p>}

      {goals && rows.length > 0 && !narrow && (
        <div className="planner-timeline-chart">
          <div className="planner-timeline-row planner-timeline-head" aria-hidden="true">
            <span className="planner-timeline-label" />
            <span className="planner-timeline-track">
              {monthList.map((m) => (
                <span key={m} className="planner-timeline-month" style={{ insetInlineStart: `${pct(m < range.from ? range.from : m)}%` }}>{monthName(m)}</span>
              ))}
              {inRange(now) && <span className="planner-timeline-today" style={{ insetInlineStart: `${pct(now)}%` }}>{t('Today')}</span>}
              {grid()}
            </span>
          </div>
          {rows.map(({ goal, targets }) => (
            <section key={goal.id} className="planner-timeline-goal" aria-label={goal.statement}>
              <div className="planner-timeline-row planner-timeline-goal-row">
                <h4 className="planner-timeline-label">{goal.statement}</h4>
                <span className="planner-timeline-track">
                  {grid()}
                  {inRange(goal.byDate) && (
                    <span className="planner-timeline-goal-mark" style={{ insetInlineStart: `${pct(goal.byDate as string)}%` }}>
                      <span aria-hidden="true">◆</span> {t('Goal due {date}', { date: d.short(goal.byDate as string) })}
                    </span>
                  )}
                </span>
              </div>
              {targets.map(({ target: x, span }) => {
                const s = span && { start: span.start < range.from ? range.from : span.start, end: span.end > range.to ? range.to : span.end };
                return (
                  <div key={x.id} className="planner-timeline-row">
                    <span className="planner-timeline-label">{x.statement}</span>
                    <span className="planner-timeline-track">
                      {grid()}
                      {span && s ? (
                        <button
                          type="button"
                          className={`planner-timeline-target is-${x.status}${span.start < range.from ? ' is-cut-start' : ''}${span.end > range.to ? ' is-cut-end' : ''}`}
                          style={{ insetInlineStart: `${pct(s.start)}%`, width: `${Math.max(1.5, pct(addDays(s.end, 1)) - pct(s.start))}%` }}
                          aria-label={barName(x, span)}
                          aria-pressed={selected === x.id}
                          onClick={() => setSelected(selected === x.id ? null : x.id)}
                        >
                          <span className="planner-timeline-target-text">{status(x)}</span>
                          {inRange(x.achievedAt) && (
                            <span className="planner-timeline-tick" style={{ insetInlineStart: `${((pct(x.achievedAt as string) - pct(s.start)) / Math.max(0.001, pct(addDays(s.end, 1)) - pct(s.start))) * 100}%` }} aria-hidden="true">✓</span>
                          )}
                        </button>
                      ) : (
                        <button type="button" className="planner-timeline-undated" aria-pressed={selected === x.id} onClick={() => setSelected(selected === x.id ? null : x.id)}>
                          {t('No review date set')}
                        </button>
                      )}
                    </span>
                  </div>
                );
              })}
            </section>
          ))}
          <ul className="planner-prog-legend planner-timeline-legend">
            <li><span className="planner-timeline-key is-WORKING" aria-hidden="true" /> {t(TARGET_STATUS_LABEL.WORKING)}</li>
            <li><span className="planner-timeline-key is-ACHIEVED" aria-hidden="true" /> {t(TARGET_STATUS_LABEL.ACHIEVED)}</li>
            <li><span className="planner-timeline-key is-NOT_ACHIEVED" aria-hidden="true" /> {t(TARGET_STATUS_LABEL.NOT_ACHIEVED)}</li>
            <li><span className="planner-timeline-key is-STOPPED" aria-hidden="true" /> {t(TARGET_STATUS_LABEL.STOPPED)}</li>
            <li><span aria-hidden="true">✓</span> {t('Day it was achieved')}</li>
            <li><span aria-hidden="true">◆</span> {t('Goal due')}</li>
          </ul>
        </div>
      )}

      {goals && rows.length > 0 && narrow && (
        events.length === 0 ? (
          <p className="muted">{t('Nothing starts, is reviewed or is due in these dates.')}</p>
        ) : (
          <div className="planner-timeline-list">
            {monthList.map((m) => {
              const list = events.filter((e) => e.day >= m && e.day <= monthEnd(m));
              if (list.length === 0) return null;
              return (
                <section key={m} className="planner-timeline-list-month">
                  <h4>{d.month(m)}</h4>
                  <ul>
                    {list.map((e, i) => (
                      <li key={i} className={now === e.day ? 'is-today' : undefined}>
                        <span className="planner-prog-date">{d.short(e.day)}</span>
                        <span className="planner-timeline-kind">{e.kind === 'achieved' ? '✓ ' : e.kind === 'goal' ? '◆ ' : ''}{EVENT_LABEL[e.kind]}</span>
                        {e.target ? (
                          <button type="button" className="planner-timeline-link" aria-pressed={selected === e.target.id} onClick={() => setSelected(e.target!.id)}>
                            {e.target.statement}
                          </button>
                        ) : (
                          <span>{e.goal.statement}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
        )
      )}

      {chosen && <Details goal={chosen.goal} target={chosen.target} onClose={() => setSelected(null)} />}
    </div>
  );
}

function Details({ goal, target: x, onClose }: { goal: Goal; target: Target; onClose: () => void }) {
  const t = useT();
  const d = useDates();
  const span = spanOf(x);
  const dateOr = (s: string | null) => (s ? d.short(s) : t('Not set'));
  return (
    <section className="card planner-timeline-details" aria-live="polite" aria-labelledby={`tl-${x.id}`}>
      <h3 id={`tl-${x.id}`}>{x.statement}</h3>
      <p className="planner-prog-sub">{t('Goal: {goal}', { goal: goal.statement })}</p>
      <dl className="planner-timeline-facts">
        <div><dt>{t('Status')}</dt><dd>{t(TARGET_STATUS_LABEL[x.status])}</dd></div>
        <div><dt>{t('Starts')}</dt><dd>{x.startDate ? d.short(x.startDate) : span ? t('About {date}', { date: d.short(span.start) }) : t('Not set')}</dd></div>
        <div><dt>{t('Review date')}</dt><dd>{dateOr(x.reviewDate)}</dd></div>
        <div><dt>{t('By')}</dt><dd>{dateOr(x.byDate)}</dd></div>
        {x.achievedAt && <div><dt>{t('Achieved on')}</dt><dd>✓ {d.short(x.achievedAt)}</dd></div>}
      </dl>
      <h4>{t('Provision')}</h4>
      {x.provisionWhat || x.provisionWho || x.provisionHowOften || x.provisionMinutes != null ? (
        <dl className="planner-timeline-facts">
          {x.provisionWhat && <div><dt>{t('What')}</dt><dd>{x.provisionWhat}</dd></div>}
          {x.provisionWho && <div><dt>{t('Who')}</dt><dd>{x.provisionWho}</dd></div>}
          {x.provisionHowOften && <div><dt>{t('How often')}</dt><dd>{x.provisionHowOften}</dd></div>}
          {x.provisionMinutes != null && <div><dt>{t('How long')}</dt><dd>{t('{n} minutes per session', { n: x.provisionMinutes })}</dd></div>}
        </dl>
      ) : (
        <p className="muted">{t('No provision written yet.')}</p>
      )}
      <h4>{t('Progress')}</h4>
      <p>{t('{done} of {planned} sessions done', { done: x.progress.done, planned: x.progress.planned })}</p>
      {x.progress.lastResult && <p>{t('Latest result: {result}', { result: x.progress.lastResult })}</p>}
      <button type="button" className="btn secondary" onClick={onClose}>{t('Close')}</button>
    </section>
  );
}
